import { ReactElement, useMemo } from 'react';
import { RiDeleteBinLine } from 'react-icons/ri';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { BPMTable, Header } from '@Components/StickyTable';
import { formatLastEdited, SavedSummary } from './streaming-media-planner-utils';

interface SavedSummariesTabProps {
  summaries: SavedSummary[];
  onOpen: (summary: SavedSummary) => void;
  onDelete: (summary: SavedSummary) => void;
}

const renderEmpty = (): ReactElement => (
  <div className='saved-list-empty'>
    No saved summaries yet. Build a view on the <strong>Summaries</strong> tab and use{' '}
    <strong>Save New Summary</strong> to save one.
  </div>
);

export const SavedSummariesTab = ({
  summaries,
  onOpen,
  onDelete
}: SavedSummariesTabProps): ReactElement => {
  const headers = useMemo<Header[]>(
    () => [
      {
        name: 'id',
        label: '#',
        width: 64,
        renderer: (summary: SavedSummary) => (
          <InteractiveDiv className='saved-list-cell' onClick={() => onOpen(summary)}>
            {summary.id}
          </InteractiveDiv>
        )
      },
      {
        name: 'name',
        label: 'Name',
        flex: 1,
        renderer: (summary: SavedSummary) => (
          <InteractiveDiv
            className='saved-list-cell saved-list-name'
            onClick={() => onOpen(summary)}
          >
            {summary.name}
          </InteractiveDiv>
        )
      },
      {
        name: 'lastEditor',
        label: 'Last Editor',
        width: 220,
        renderer: (summary: SavedSummary) => (
          <InteractiveDiv className='saved-list-cell' onClick={() => onOpen(summary)}>
            {summary.lastEditor || '—'}
          </InteractiveDiv>
        )
      },
      {
        name: 'lastEditedAt',
        label: 'Last Edited',
        width: 180,
        contentGetter: (summary: SavedSummary) => summary.lastEditedAt,
        renderer: (summary: SavedSummary) => (
          <InteractiveDiv className='saved-list-cell' onClick={() => onOpen(summary)}>
            {formatLastEdited(summary.lastEditedAt)}
          </InteractiveDiv>
        )
      },
      {
        name: 'actions',
        label: '',
        width: 56,
        nonInteractive: true,
        nonFilterable: true,
        renderer: (summary: SavedSummary) => (
          <Button
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={() => onDelete(summary)}
            tooltip={{ text: 'Delete summary' }}
          >
            <RiDeleteBinLine />
          </Button>
        )
      }
    ],
    [onOpen, onDelete]
  );

  return (
    <div className='saved-list-container'>
      <BPMTable
        data={summaries}
        headers={headers}
        filterBar={false}
        rowHeight={44}
        noRowsRenderer={renderEmpty}
      />
    </div>
  );
};
